import { Capacitor } from "@capacitor/core";

/**
 * Thin wrapper over @capacitor-community/native-audio so timer beeps
 * play through iOS's AVAudioPlayer (which respects the AVAudioSession
 * `.ambient` + `.mixWithOthers` configuration set in AppDelegate.swift)
 * instead of through WKWebView's audio path (which re-grabs the session
 * as `.playback` at play time and kills the user's music — the bug v1.1.3
 * tried to fix in native and failed because WKWebView overrides the
 * app's setting at runtime).
 *
 * Pre-loads a single short triple-beep WAV at startup so play() at timer
 * end is synchronous from the user's perspective. Web is a no-op (timer
 * code falls through to the existing WebAudio/HTMLAudio path).
 *
 * Gated on iOS build ≥ 18 via audioCapability.ts — older IPAs don't have
 * the @capacitor-community/native-audio plugin compiled in and would crash
 * if we tried to call it.
 *
 * On asset path resolution: the plugin's preloadAsset
 * (ios/Sources/NativeAudio/Plugin.swift:242-243) does
 *   let split = assetPath.components(separatedBy: ".")
 *   Bundle.main.path(forResource: split[0], ofType: split[1])
 * which is documented to support subdirectory paths in the resource
 * name — but in practice fails silently for some Capacitor IPAs
 * depending on whether `public/` is registered as a folder reference
 * vs a group, the exact Xcode build phase order, and iOS version.
 * Founder report 2026-06-04: v1.1.75 (with assetPath = "public/sounds/beep.wav")
 * still produced no beep. Rather than guess the One True Path, we try
 * each candidate in order and stick with whichever one preload accepts.
 */

const BEEP_ASSET_ID = "trybeup-beep";

// Ordered list of paths to try at preload. First one whose preload()
// resolves wins and is what play() ends up addressing (via the assetId).
const BEEP_ASSET_PATH_CANDIDATES = [
  "public/sounds/beep.wav",
  "sounds/beep.wav",
  "beep.wav",
];

let initialised = false;
let ready = false;
let lastInitError: string | null = null;
let lastResolvedPath: string | null = null;
let lastPlayResult: "never" | "ok" | string = "never";

/**
 * Configure + preload at app boot. Fire-and-forget; failure leaves
 * `ready` false and the JS layer falls back to haptics-only via
 * audioCapability.ts.
 */
export async function initNativeAudio(): Promise<void> {
  if (initialised || !Capacitor.isNativePlatform()) return;
  initialised = true;
  try {
    const { NativeAudio } = await import("@capacitor-community/native-audio");
    // focus: false → mix with other apps' audio (don't grab audio focus,
    // don't duck Pandora / Spotify / YouTube). Without this, the plugin
    // briefly activates `.playback` and the very problem we're fixing
    // re-appears.
    await NativeAudio.configure({ focus: false, fade: false }).catch(() => {});

    // Try each candidate path. The plugin's resource lookup behaviour
    // depends on bundle layout details that are easier to probe than
    // to predict — see header comment.
    //
    // "Audio Asset already exists" is the plugin's error when the
    // assetId is already registered on the native side. That happens
    // every time we Capgo-hotswap to a new JS bundle: the previous
    // bundle preloaded successfully, the registration lives on the
    // native side, the new bundle's init tries to preload again and
    // hits the duplicate guard. Treat it as a SUCCESS for the path
    // that triggered it — that asset is already loaded and ready to
    // play (founder report 2026-06-04: the diagnostic chip showed
    // exactly this on bundle 1.1.77 — `public/sounds/beep.wav: Audio
    // Asset already exists`).
    //
    // Do NOT unload between candidates anymore — that was the v1.1.77
    // bug. Unloading a working registration to retry with the next
    // (probably non-existent) candidate burned the only path that
    // works.
    const errors: string[] = [];
    for (const candidate of BEEP_ASSET_PATH_CANDIDATES) {
      try {
        await NativeAudio.preload({
          assetId: BEEP_ASSET_ID,
          assetPath: candidate,
          audioChannelNum: 1,
          volume: 1.0,
        });
        ready = true;
        lastResolvedPath = candidate;
        return;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        if (msg.includes("Audio Asset already exists")) {
          ready = true;
          lastResolvedPath = `${candidate} (cached from previous bundle)`;
          return;
        }
        errors.push(`${candidate}: ${msg}`);
      }
    }
    lastInitError = errors.join(" | ");
    // eslint-disable-next-line no-console
    console.warn("[native-audio] all asset paths failed:", lastInitError);
  } catch (err) {
    // Plugin missing (older IPA), file missing, or preload error.
    // canPlayMixedAudio() build-gate should already prevent us from
    // getting here on those builds — this is a defence-in-depth catch.
    lastInitError = err instanceof Error ? err.message : String(err);
    // eslint-disable-next-line no-console
    console.warn("[native-audio] init failed", err);
  }
}

/**
 * Play the preloaded beep. Returns synchronously (the underlying
 * `NativeAudio.play()` promise is fire-and-forget).
 */
export function playNativeBeep(): void {
  if (!ready) {
    lastPlayResult = "not-ready";
    return;
  }
  // Don't await — keep callers (which fire from rest-end / timer-end
  // ticks) snappy. NativeAudio.play() is non-blocking on the JS side
  // anyway; the native side schedules the AVAudioPlayer on its own queue.
  // We DO capture the result for the diagnostic chip so a "preload OK
  // but play silent" situation (silent switch, volume 0, plugin
  // misbehaviour) is distinguishable from a preload failure.
  import("@capacitor-community/native-audio")
    .then(({ NativeAudio }) => {
      NativeAudio.play({ assetId: BEEP_ASSET_ID })
        .then(() => { lastPlayResult = "ok"; })
        .catch((err: unknown) => {
          lastPlayResult = err instanceof Error ? err.message : String(err);
        });
    })
    .catch((err: unknown) => {
      lastPlayResult = err instanceof Error ? err.message : String(err);
    });
}

export function isNativeAudioReady(): boolean {
  return ready;
}

/**
 * For the in-app diagnostic display in AppVersionFooter. Surfaces the
 * exact preload error so a founder seeing "haptic but no beep" can read
 * it directly off the footer instead of digging through device logs.
 */
export function getNativeAudioDiagnostics(): {
  initialised: boolean;
  ready: boolean;
  resolvedPath: string | null;
  lastError: string | null;
  lastPlayResult: typeof lastPlayResult;
} {
  return {
    initialised,
    ready,
    resolvedPath: lastResolvedPath,
    lastError: lastInitError,
    lastPlayResult,
  };
}
